import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sparkles, Check, AlertTriangle, Upload } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PLAUS_COLOR = {
  high: "text-[hsl(var(--success))]",
  medium: "text-[hsl(var(--warning))]",
  low: "text-destructive",
};

export function QuickAddModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});

  const reset = () => {
    setEmail("");
    setPreview(null);
    setOverrides({});
  };

  const doPreview = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/enrichment/preview", { email });
      setPreview(data);
      setOverrides({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/enrichment/create-lead", {
        email,
        overrides: Object.keys(overrides).length ? overrides : undefined,
      });
      if (data.created) {
        toast.success(`Created ${data.lead.full_name} at ${data.lead.company}`);
        onCreated(data.lead);
        setOpen(false);
        reset();
      } else if (data.reason === "duplicate") {
        toast.error("This email is already in your leads");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const setOv = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));
  const val = (k) => (overrides[k] !== undefined ? overrides[k] : preview?.data?.[k] || "");

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="quick-add-btn" style={{ borderRadius: 2 }}>
          <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} /> Quick add (AI)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" style={{ borderRadius: 2 }} data-testid="quick-add-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>Quick add — AI enrichment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Paste a CFO's email. The AI infers their company, industry, employee range, revenue band, multi-site status, location, and a plausible LinkedIn URL — usually in 5 seconds.
          </p>
          <div className="flex gap-2">
            <Input
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doPreview()}
              placeholder="sarah.chen@meridianlogistics.com.au"
              className="input-crisp"
              data-testid="quick-add-email"
            />
            <Button onClick={doPreview} disabled={previewing} data-testid="quick-add-preview" style={{ borderRadius: 2 }}>
              {previewing ? "Enriching…" : "Enrich"}
            </Button>
          </div>

          {preview && (
            <div className="border border-border bg-card p-5 space-y-4 fade-up" style={{ borderRadius: 2 }}>
              {preview.warning && (
                <div className="flex items-start gap-2 text-sm text-[hsl(var(--warning))] border border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.06)] p-3" style={{ borderRadius: 2 }}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>{preview.warning}</div>
                </div>
              )}
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="overline">Enriched preview</div>
                <div className={`text-xs font-mono uppercase tracking-widest ${PLAUS_COLOR[preview.data.plausibility] || ""}`}>
                  Plausibility · {preview.data.plausibility || "medium"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Full name</Label>
                  <Input value={overrides.full_name ?? preview.parsed_name ?? ""} onChange={(e) => setOv("full_name", e.target.value)} className="mt-1 input-crisp" data-testid="qa-name" />
                </div>
                <div>
                  <Label className="overline">Title</Label>
                  <Input value={val("title")} onChange={(e) => setOv("title", e.target.value)} className="mt-1 input-crisp" data-testid="qa-title" />
                </div>
                <div className="col-span-2">
                  <Label className="overline">Company</Label>
                  <Input value={val("company")} onChange={(e) => setOv("company", e.target.value)} className="mt-1 input-crisp" data-testid="qa-company" />
                </div>
                <div>
                  <Label className="overline">Industry</Label>
                  <Input value={val("industry")} onChange={(e) => setOv("industry", e.target.value)} className="mt-1 input-crisp" data-testid="qa-industry" />
                </div>
                <div>
                  <Label className="overline">Employees</Label>
                  <Input value={val("employees")} onChange={(e) => setOv("employees", e.target.value)} className="mt-1 input-crisp" />
                </div>
                <div>
                  <Label className="overline">Revenue</Label>
                  <Input value={val("revenue")} onChange={(e) => setOv("revenue", e.target.value)} className="mt-1 input-crisp" />
                </div>
                <div>
                  <Label className="overline">Location</Label>
                  <Input value={val("location")} onChange={(e) => setOv("location", e.target.value)} className="mt-1 input-crisp" />
                </div>
                <div className="col-span-2">
                  <Label className="overline">Website</Label>
                  <Input value={val("website")} onChange={(e) => setOv("website", e.target.value)} className="mt-1 input-crisp" />
                </div>
                <div className="col-span-2">
                  <Label className="overline">LinkedIn</Label>
                  <Input value={val("linkedin_url") || ""} onChange={(e) => setOv("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" className="mt-1 input-crisp" />
                </div>
              </div>

              {preview.data.rationale && (
                <div className="text-xs text-muted-foreground border-t border-border pt-2">
                  <span className="overline">AI rationale</span>
                  <div className="mt-1">{preview.data.rationale}</div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button variant="outline" onClick={reset} style={{ borderRadius: 2 }}>Reset</Button>
                <Button onClick={create} disabled={creating} data-testid="qa-create" style={{ borderRadius: 2 }}>
                  <Check className="h-4 w-4 mr-1" strokeWidth={1.5} /> {creating ? "Creating…" : "Create lead"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BulkImportModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setText(""); setResult(null); };

  const submit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/enrichment/bulk", { emails: text });
      setResult(data);
      toast.success(`Created ${data.summary.created} of ${data.summary.total}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-import-btn" style={{ borderRadius: 2 }}>
          <Upload className="h-4 w-4 mr-1" strokeWidth={1.5} /> Bulk import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" style={{ borderRadius: 2 }} data-testid="bulk-import-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>Bulk import — AI enrichment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste up to 25 CFO emails (one per line, or comma-separated). The AI enriches and creates a lead for each in parallel.
          </p>
          <Textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"sarah.chen@meridianlogistics.com.au\njames.obrien@nordic-aged-care.com.au\npriya@brightforge.com.au"}
            className="input-crisp font-mono text-sm"
            data-testid="bulk-textarea"
          />
          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting || !text.trim()} data-testid="bulk-submit" style={{ borderRadius: 2 }}>
              <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} /> {submitting ? "Enriching…" : "Enrich & create"}
            </Button>
          </div>

          {result && (
            <div className="border border-border bg-secondary/30 p-4 fade-up" style={{ borderRadius: 2 }}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Result</div>
                <div className="font-mono text-xs">
                  {result.summary.created} created · {result.summary.duplicates} dup · {result.summary.errors} errors
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1 text-xs">
                {result.results.map((r, i) => (
                  <div key={i} className={`flex justify-between gap-2 py-1 border-b border-border last:border-0 ${r.ok ? "" : "text-muted-foreground"}`}>
                    <span className="font-mono truncate">{r.email}</span>
                    <span className="truncate">
                      {r.ok ? `→ ${r.company} · ${r.industry}` : `✗ ${r.error}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
